import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListShell } from "@/components/shared/ListShell";
import { ListPagination } from "@/components/shared/ListPagination";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { RejectBusinessDialog } from "@/components/businesses/RejectBusinessDialog";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import {
  type BusinessListItem,
  useBusinessList,
  useVerifyBusiness,
} from "@/hooks/useBusinesses";
import { formatDate } from "@/utils/format";
import { Button } from "@/components/ui/button";

type TabKey = "pending" | "history";

const DEFAULT_PAGE_SIZE = 50;

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "history", label: "History" },
];

export const ApprovalsList = () => {
  const [tab, setTab] = useState<TabKey>("pending");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [rejectTarget, setRejectTarget] = useState<BusinessListItem | null>(
    null,
  );

  const { data, isLoading, error, refetch } = useBusinessList({
    page,
    limit,
    status: tab === "pending" ? "PENDING" : undefined,
  });
  const verify = useVerifyBusiness();
  const rows = (data?.data ?? []).filter((b) =>
    tab === "history" ? b.status !== "PENDING" : b.status === "PENDING",
  );
  const pagination = data?.pagination;

  const handleApprove = async (b: BusinessListItem) => {
    try {
      await verify.mutateAsync({ id: b.id, status: "APPROVED" });
      toast.success(`${b.businessName} approved`);
      refetch();
    } catch (e: any) {
      if (e?.data?.code === "DOCUMENTS_NOT_REVIEWED") {
        toast.error(
          "Review all documents first. Open the business to mark them viewed.",
        );
      } else {
        toast.error((e as Error).message);
      }
    }
  };

  const handleReject = async (reasons: string[], note?: string) => {
    if (!rejectTarget) return;
    try {
      await verify.mutateAsync({
        id: rejectTarget.id,
        status: "REJECTED",
        reasons,
        note,
      });
      toast.success(`${rejectTarget.businessName} rejected`);
      setRejectTarget(null);
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const columns: Array<DataTableColumn<BusinessListItem>> = [
    {
      key: "business",
      header: "Business",
      render: (b) => (
        <>
          <Link
            to={`/businesses/${b.id}`}
            className="font-medium hover:underline"
          >
            {b.businessName}
          </Link>
          <div className="text-xs text-muted-foreground truncate max-w-xs">
            {b.nameEn} · {b.city}
          </div>
        </>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (b) => <StatusBadge status={b.status} />,
    },
    {
      key: "owner",
      header: "Owner",
      render: (b) => (
        <span className="text-muted-foreground">{b.owner.name}</span>
      ),
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (b) => (
        <span className="text-muted-foreground">{formatDate(b.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (b) =>
        tab === "pending" ? (
          <div
            className="flex gap-2 justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <PermissionGate module="approvals" action="approve">
              <Button
                size="sm"
                disabled={verify.isPending}
                onClick={() => handleApprove(b)}
              >
                Approve
              </Button>
            </PermissionGate>
            <PermissionGate module="approvals" action="reject">
              <Button
                size="sm"
                variant="destructive"
                disabled={verify.isPending}
                onClick={() => setRejectTarget(b)}
              >
                Reject
              </Button>
            </PermissionGate>
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
  ];

  return (
    <AppLayout title="Approvals">
      <div className="flex gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={`px-3 py-1.5 text-sm rounded-btn border ${
              tab === t.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle={
          tab === "pending" ? "No pending approvals" : "No approval history"
        }
      >
        <DataTable columns={columns} rows={rows} rowKey={(b) => b.id} />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="businesses"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>

      <RejectBusinessDialog
        open={!!rejectTarget}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        businessName={rejectTarget?.businessName}
        onSubmit={handleReject}
        submitting={verify.isPending}
      />
    </AppLayout>
  );
};

export default ApprovalsList;
